import { Col, Modal, Row, Tabs } from 'antd';
import type { TabsProps } from 'antd';
import { Formik } from 'formik';
import { Form } from 'formik-antd';
import { useEffect, useState } from 'react';
import { BsCheckLg } from 'react-icons/bs';
import { FaRegWindowClose } from 'react-icons/fa';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import IntlMessages from 'src/domain/utility/IntlMessages';
import { GetAllReasonDescriptionsListDto } from 'src/shared/services/product-rejection-reasons/dto/get-all-reason-descriptions-list-dto';
import { schema } from './schema';
import { productRejectionReasonsService } from 'src/shared/services/product-rejection-reasons';
import LanguageCode from 'src/shared/services/types/language-code';
import OptionModalLanguage from './option-modal-language';
import languageData from 'src/domain/core/AppLanguageSwitcher/data';
import OptionModalStandard from './option-modal-standard';
import Button from 'src/shared/components/button';
import MainUtils from 'src/shared/utils/main';
import { getLocaleValue } from 'src/shared/functions/change-locales';

interface IRejectionReasonsOptionModalProps {
  visible: boolean;
  rejectionReasonsOptionDetails?: GetAllReasonDescriptionsListDto;
  onCancel: () => void;
  onFinish: () => void;
}

const RejectionReasonsOptionModal = (
  props: IRejectionReasonsOptionModalProps,
) => {
  const { visible, rejectionReasonsOptionDetails, onCancel, onFinish } = props;

  const { messages } = useIntl();

  const { id } = useParams();

  const [initialValuesForm, setInitialValuesForm] = useState<any>();

  const [activeKey, setActiveKey] = useState('');

  useEffect(() => {
    setActiveKey('standard');
    if (rejectionReasonsOptionDetails && visible)
      setInitialValuesForm({
        value: rejectionReasonsOptionDetails?.Value,
        reasonId: id ?? '',
        locales:
          rejectionReasonsOptionDetails?.Locales?.length > 0
            ? rejectionReasonsOptionDetails?.Locales?.sort((a: any, b: any) =>
              a.name?.localeCompare(b.name),
            ).map((item) => {
              return {
                id: item.Id,
                languageId: item.LanguageId,
                localeKey: item.LocaleKey,
                localeValue: item.LocaleValue,
              };
            })
            : [],
      });
    else
      setInitialValuesForm({
        value: '',
        id: undefined,
        locales: [],
      });
  }, [rejectionReasonsOptionDetails, visible]);

  const handleSubmit = async (
    values: any,
    setSubmitting: any,
    resetForm: any,
  ) => {
    try {
      if (!rejectionReasonsOptionDetails) {
        await productRejectionReasonsService.addNewRejectionReasonDescription({
          ...values,
          reasonId: id,
          value: !MainUtils.isEmptyValue(
            getLocaleValue(values?.locales, LanguageCode.English, 'value'),
          )
            ? getLocaleValue(values?.locales, LanguageCode.English, 'value')
            : getLocaleValue(values?.locales, LanguageCode.Arabic, 'value'),
        });
      } else {
        await productRejectionReasonsService.updateRejectionReasonDescription(
          rejectionReasonsOptionDetails.Id ?? '',
          {
            ...values,
            value: !MainUtils.isEmptyValue(
              getLocaleValue(values?.locales, LanguageCode.English, 'value'),
            )
              ? getLocaleValue(values?.locales, LanguageCode.English, 'value')
              : getLocaleValue(values?.locales, LanguageCode.Arabic, 'value'),
          },
        );
      }
      resetForm();
      await onFinish();
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const langComponent = [
    {
      code: `${LanguageCode.English}`,
      langComponent: (
        <OptionModalLanguage code={LanguageCode.English} index={0} />
      ),
    },
    {
      code: `${LanguageCode.Español}`,
      langComponent: (
        <OptionModalLanguage index={1} code={LanguageCode.Español} />
      ),
    },
    {
      code: `${LanguageCode.Arabic}`,
      langComponent: (
        <OptionModalLanguage index={2} code={LanguageCode.Arabic} />
      ),
    },
  ];

  const itemsTab: TabsProps['items'] = [
    {
      key: 'standard',
      label: `${messages[
        'specificationAttributes.edit.option.standard'
      ].toString()}`,
      children: <OptionModalStandard />,
    },
    ...languageData
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((language) => {
        return {
          key: language.code,
          label: (
            <>
              <Row gutter={7}>
                <Col>
                  <i className={`flag flag-24 flag-${language.icon}`} />
                </Col>
                <Col>
                  <h4 className='title-tab'>{language.name}</h4>
                </Col>
              </Row>
            </>
          ),
          children: (
            <>
              {langComponent && langComponent?.length > 0 ? (
                langComponent?.find((item) => item.code === language.code)
                  ?.langComponent ?? <></>
              ) : (
                <></>
              )}
            </>
          ),
        };
      }),
  ];

  return (
    <Formik
      enableReinitialize
      initialValues={initialValuesForm}
      validationSchema={schema(messages)}
      onSubmit={(values, { setSubmitting, resetForm }: any) =>
        handleSubmit(values, setSubmitting, resetForm)
      }
      validateOnChange={false}
      validateOnBlur={false}>
      {({ handleSubmit, isSubmitting }: any) => (
        <Form>
          <Modal
            className='add-option-modal'
            width={'700px'}
            open={visible}
            maskClosable={false}
            title={
              <>
                <div className='text-title'>
                  <span className='icon-title'>
                    <FaRegWindowClose />
                  </span>
                  {rejectionReasonsOptionDetails ? (
                    <IntlMessages id='specificationAttributes.edit.editOptionTitleModal' />
                  ) : (
                    <IntlMessages id='specificationAttributes.edit.addOptionTitleModal' />
                  )}
                </div>
              </>
            }
            onCancel={onCancel}
            footer={false}>
            <Row gutter={[0, 20]}>
              <Col span={24}>
                <Row justify='end'>
                  <Col>
                    <Button
                      icon={<BsCheckLg className='add-button-icon' />}
                      onClickAction={handleSubmit}
                      text={messages['common.save'].toString()}
                      type='primary'
                      size='small'
                      className='add-button'
                      loading={isSubmitting}
                    />
                  </Col>
                </Row>
              </Col>

              <Col span={24}>
                <Tabs
                  activeKey={activeKey}
                  onChange={(item) => setActiveKey(item)}
                  items={itemsTab}
                />
              </Col>
            </Row>
          </Modal>
        </Form>
      )}
    </Formik>
  );
};

export default RejectionReasonsOptionModal;
